import { useEffect, useMemo, useRef } from "react";
import { IdleDeadline } from "../lib/idle.js";

const ACTIVITY: ReadonlyArray<keyof WindowEventMap> = ["pointerdown", "keydown", "wheel", "touchstart"];

export interface IdleHandle {
  /** Milliseconds until the idle lock fires; undefined while inactive. */
  readonly remainingMs: () => number | undefined;
}

/**
 * Calls onIdle after the given minutes without user input. Input pushes the deadline out,
 * but never past an expired one, so throttled background tabs still lock on return.
 */
export function useIdleTimeout(active: boolean, minutes: number, onIdle: () => void): IdleHandle {
  const onIdleRef = useRef(onIdle);
  const deadlineRef = useRef<IdleDeadline | undefined>(undefined);

  useEffect((): void => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect((): (() => void) | undefined => {
    if (!active) {
      deadlineRef.current = undefined;
      return undefined;
    }
    const deadline = new IdleDeadline(minutes * 60_000, Date.now());
    deadlineRef.current = deadline;
    let fired = false;
    let timer = 0;

    function fire(): void {
      if (!fired) {
        fired = true;
        onIdleRef.current();
      }
    }

    function check(): void {
      if (deadline.expired(Date.now())) {
        fire();
      } else {
        window.clearTimeout(timer);
        timer = window.setTimeout(check, deadline.remaining(Date.now()));
      }
    }

    function onActivity(): void {
      if (!deadline.touch(Date.now())) {
        fire();
      }
    }

    function onVisibility(): void {
      if (document.visibilityState === "visible") {
        check();
      }
    }

    check();
    for (const name of ACTIVITY) {
      window.addEventListener(name, onActivity, { passive: true, capture: true });
    }
    document.addEventListener("visibilitychange", onVisibility);
    return (): void => {
      window.clearTimeout(timer);
      for (const name of ACTIVITY) {
        window.removeEventListener(name, onActivity, { capture: true });
      }
      document.removeEventListener("visibilitychange", onVisibility);
      if (deadlineRef.current === deadline) {
        deadlineRef.current = undefined;
      }
    };
  }, [active, minutes]);

  return useMemo(
    (): IdleHandle => ({
      remainingMs: (): number | undefined => deadlineRef.current?.remaining(Date.now())
    }),
    []
  );
}

/** Locks once the page has been hidden for graceMs. Checked on return too, since hidden-tab timers are throttled. */
export function useHiddenLock(active: boolean, graceMs: number, onLock: () => void): void {
  const onLockRef = useRef(onLock);

  useEffect((): void => {
    onLockRef.current = onLock;
  }, [onLock]);

  useEffect((): (() => void) | undefined => {
    if (!active) {
      return undefined;
    }
    let hiddenAt: number | undefined;
    let timer = 0;

    function onVisibility(): void {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        timer = window.setTimeout((): void => onLockRef.current(), graceMs);
        return;
      }
      window.clearTimeout(timer);
      if (hiddenAt !== undefined && Date.now() - hiddenAt >= graceMs) {
        onLockRef.current();
      }
      hiddenAt = undefined;
    }

    document.addEventListener("visibilitychange", onVisibility);
    return (): void => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active, graceMs]);
}
